import React, { useEffect, useState, useCallback } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import ptBrLocale from '@fullcalendar/core/locales/pt-br';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { 
  Calendar, Plus, Users, MapPin, Clock, Video, Building,
  User, Phone, Mail, FileText, CheckCircle, XCircle, Loader2,
  CalendarDays, Search, AlertCircle
} from 'lucide-react';
import { canEdit, getUser } from '@/lib/auth';

export default function Agenda() {
  const user = getUser();
  const [loading, setLoading] = useState(true);
  const [eventos, setEventos] = useState([]);
  const [reunioes, setReunioes] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  
  // Dialog states
  const [novaReuniaoDialogOpen, setNovaReuniaoDialogOpen] = useState(false);
  const [detalhesDialogOpen, setDetalhesDialogOpen] = useState(false);
  const [selectedReuniao, setSelectedReuniao] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    titulo: '',
    data: '',
    hora_inicio: '08:00',
    hora_fim: '09:00',
    student_id: '',
    local: 'Sala de Reuniões',
    tipo: 'presencial',
    link_online: '',
    assunto: '',
    pauta: '',
    observacoes: '',
    professores_ids: []
  });
  const [saving, setSaving] = useState(false);
  
  // Data for selects
  const [students, setStudents] = useState([]);
  const [professores, setProfessores] = useState([]);
  const [searchStudent, setSearchStudent] = useState('');
  
  const loadEventos = useCallback(async () => {
    try {
      const mes = currentDate.getMonth() + 1;
      const ano = currentDate.getFullYear();
      
      const res = await api.get('/reunioes/calendario/eventos', {
        params: { mes, ano }
      });
      setEventos(res.data);
    } catch (error) {
      console.error('Erro ao carregar eventos:', error);
    }
  }, [currentDate]);
  
  const loadReunioes = useCallback(async () => {
    try {
      const res = await api.get('/reunioes');
      setReunioes(res.data);
    } catch (error) {
      console.error('Erro ao carregar reuniões:', error);
    } finally {
      setLoading(false);
    }
  }, []);
  
  const loadStudents = async () => {
    try {
      const res = await api.get('/students');
      setStudents(res.data);
    } catch (error) {
      console.error('Erro ao carregar alunos:', error);
    }
  };
  
  const loadProfessores = async () => {
    try {
      const res = await api.get('/reunioes/professores/disponiveis');
      setProfessores(res.data);
    } catch (error) {
      console.error('Erro ao carregar professores:', error);
    }
  };
  
  useEffect(() => {
    loadEventos();
    loadReunioes();
    loadStudents();
    loadProfessores();
  }, [loadEventos, loadReunioes]);
  
  const handleDateClick = (arg) => {
    if (!canEdit()) return;
    
    setFormData({
      ...formData,
      data: arg.dateStr,
      titulo: '',
      hora_inicio: '08:00',
      hora_fim: '09:00',
      student_id: '',
      local: 'Sala de Reuniões',
      tipo: 'presencial',
      assunto: '',
      professores_ids: []
    });
    setNovaReuniaoDialogOpen(true);
  };
  
  const handleEventClick = async (arg) => {
    try {
      const res = await api.get(`/reunioes/${arg.event.id}`);
      setSelectedReuniao(res.data);
      setDetalhesDialogOpen(true);
    } catch (error) {
      toast.error('Erro ao carregar detalhes da reunião');
    }
  };
  
  const handleDatesSet = (arg) => {
    const midDate = new Date((arg.start.getTime() + arg.end.getTime()) / 2);
    setCurrentDate(midDate);
  };
  
  const handleCreateReuniao = async () => {
    if (!formData.titulo || !formData.data || !formData.hora_inicio) {
      toast.error('Preencha os campos obrigatórios');
      return;
    }
    
    setSaving(true);
    try {
      await api.post('/reunioes', formData);
      toast.success('Reunião agendada com sucesso!');
      setNovaReuniaoDialogOpen(false);
      loadEventos();
      loadReunioes();
      
      // Reset form
      setFormData({
        titulo: '',
        data: '',
        hora_inicio: '08:00',
        hora_fim: '09:00',
        student_id: '',
        local: 'Sala de Reuniões',
        tipo: 'presencial',
        link_online: '',
        assunto: '',
        pauta: '',
        observacoes: '',
        professores_ids: []
      });
    } catch (error) {
      toast.error('Erro ao criar reunião');
    } finally {
      setSaving(false);
    }
  };
  
  const handleCancelReuniao = async () => {
    if (!selectedReuniao) return;
    if (!window.confirm('Deseja realmente cancelar esta reunião?')) return;
    
    try {
      await api.delete(`/reunioes/${selectedReuniao.id}`);
      toast.success('Reunião cancelada');
      setDetalhesDialogOpen(false);
      loadEventos();
      loadReunioes();
    } catch (error) {
      toast.error('Erro ao cancelar reunião');
    }
  };
  
  const handleToggleProfessor = (profId) => {
    setFormData(prev => {
      const ids = prev.professores_ids || [];
      if (ids.includes(profId)) {
        return { ...prev, professores_ids: ids.filter(id => id !== profId) };
      }
      return { ...prev, professores_ids: [...ids, profId] };
    });
  };
  
  const filteredStudents = students.filter(s => 
    s.name?.toLowerCase().includes(searchStudent.toLowerCase()) ||
    s.registration_number?.includes(searchStudent)
  ).slice(0, 50);
  
  const getStatusBadge = (status) => {
    const styles = {
      agendada: 'bg-blue-100 text-blue-800',
      confirmada: 'bg-green-100 text-green-800',
      reagendada: 'bg-amber-100 text-amber-800',
      realizada: 'bg-gray-100 text-gray-800',
      cancelada: 'bg-red-100 text-red-800'
    };
    return <Badge className={styles[status] || 'bg-gray-100'}>{status}</Badge>;
  };
  
  const proximasReunioes = reunioes
    .filter(r => r.status !== 'cancelada' && r.data >= new Date().toISOString().split('T')[0])
    .sort((a, b) => a.data.localeCompare(b.data) || a.hora_inicio.localeCompare(b.hora_inicio))
    .slice(0, 5);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2" data-testid="agenda-title">
            <CalendarDays className="w-7 h-7 text-purple-600" />
            Agenda de Reuniões
          </h1>
          <p className="text-muted-foreground">Reuniões com famílias e equipe</p>
        </div>
        {canEdit() && (
          <Button onClick={() => setNovaReuniaoDialogOpen(true)} className="bg-purple-600 hover:bg-purple-700">
            <Plus className="w-4 h-4 mr-2" />
            Nova Reunião
          </Button>
        )}
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Calendário */}
        <div className="lg:col-span-3">
          <Card>
            <CardContent className="p-4">
              <FullCalendar
                plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                initialView="dayGridMonth"
                locale={ptBrLocale}
                headerToolbar={{
                  left: 'prev,next today',
                  center: 'title',
                  right: 'dayGridMonth,timeGridWeek,timeGridDay'
                }}
                events={eventos}
                dateClick={handleDateClick}
                eventClick={handleEventClick}
                datesSet={handleDatesSet}
                height="auto"
                eventTimeFormat={{
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false
                }}
                slotMinTime="07:00:00"
                slotMaxTime="20:00:00"
                allDaySlot={false}
                selectable={canEdit()}
                editable={false}
                eventDisplay="block"
                dayMaxEvents={3}
              />
            </CardContent>
          </Card>
        </div>
        
        {/* Próximas Reuniões */}
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Próximas Reuniões
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {proximasReunioes.length > 0 ? (
                proximasReunioes.map(reuniao => (
                  <div 
                    key={reuniao.id}
                    className="p-3 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                    onClick={() => {
                      setSelectedReuniao(reuniao);
                      setDetalhesDialogOpen(true);
                    }}
                  >
                    <p className="font-medium text-sm truncate">{reuniao.titulo}</p>
                    <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                      <Calendar className="w-3 h-3" />
                      {new Date(reuniao.data + 'T12:00:00').toLocaleDateString('pt-BR')}
                      <span>•</span>
                      {reuniao.hora_inicio}
                    </div>
                    {reuniao.student_name && (
                      <p className="text-xs text-muted-foreground mt-1 truncate">
                        <User className="w-3 h-3 inline mr-1" />
                        {reuniao.student_name}
                      </p>
                    )}
                  </div>
                ))
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">
                  Nenhuma reunião agendada
                </p>
              )}
            </CardContent>
          </Card>
          
          {/* Estatísticas */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Resumo do Mês</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Total</span>
                <span className="font-semibold">{eventos.length}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Agendadas</span>
                <span className="font-semibold text-blue-600">
                  {eventos.filter(e => e.extendedProps?.status === 'agendada').length}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Confirmadas</span>
                <span className="font-semibold text-green-600">
                  {eventos.filter(e => e.extendedProps?.status === 'confirmada').length}
                </span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      
      {/* Dialog Nova Reunião */}
      <Dialog open={novaReuniaoDialogOpen} onOpenChange={setNovaReuniaoDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-purple-600" />
              Agendar Reunião
            </DialogTitle>
            <DialogDescription>
              Agende uma reunião com a família de um aluno
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Título */}
            <div className="space-y-2">
              <Label>Título da Reunião *</Label>
              <Input
                placeholder="Ex: Reunião de Acompanhamento"
                value={formData.titulo}
                onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
              />
            </div>
            
            {/* Data e Hora */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Data *</Label>
                <Input
                  type="date"
                  value={formData.data}
                  onChange={(e) => setFormData({ ...formData, data: e.target.value })}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
              <div className="space-y-2">
                <Label>Hora Início *</Label>
                <Input
                  type="time"
                  value={formData.hora_inicio}
                  onChange={(e) => setFormData({ ...formData, hora_inicio: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Hora Fim</Label>
                <Input
                  type="time"
                  value={formData.hora_fim}
                  onChange={(e) => setFormData({ ...formData, hora_fim: e.target.value })}
                />
              </div>
            </div>
            
            {/* Aluno */}
            <div className="space-y-2">
              <Label>Aluno (opcional)</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar aluno por nome ou matrícula..."
                  value={searchStudent}
                  onChange={(e) => setSearchStudent(e.target.value)}
                  className="pl-10"
                />
              </div>
              {searchStudent && filteredStudents.length > 0 && (
                <div className="border rounded-lg max-h-40 overflow-y-auto">
                  {filteredStudents.map(student => (
                    <div
                      key={student.id}
                      className={`p-2 hover:bg-muted cursor-pointer ${formData.student_id === student.id ? 'bg-purple-50' : ''}`}
                      onClick={() => {
                        setFormData({ ...formData, student_id: student.id });
                        setSearchStudent(student.name);
                      }}
                    >
                      <p className="text-sm font-medium">{student.name}</p>
                      <p className="text-xs text-muted-foreground">{student.class_name}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Local e Tipo */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tipo</Label>
                <Select 
                  value={formData.tipo} 
                  onValueChange={(v) => setFormData({ ...formData, tipo: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="presencial">
                      <span className="flex items-center gap-2">
                        <Building className="w-4 h-4" /> Presencial
                      </span>
                    </SelectItem>
                    <SelectItem value="online">
                      <span className="flex items-center gap-2">
                        <Video className="w-4 h-4" /> Online
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Local</Label>
                <Input
                  placeholder="Ex: Sala de Reuniões"
                  value={formData.local}
                  onChange={(e) => setFormData({ ...formData, local: e.target.value })}
                />
              </div>
            </div>
            
            {/* Link Online (se tipo = online) */}
            {formData.tipo === 'online' && (
              <div className="space-y-2">
                <Label>Link da Reunião Online</Label>
                <Input
                  placeholder="https://meet.google.com/..."
                  value={formData.link_online}
                  onChange={(e) => setFormData({ ...formData, link_online: e.target.value })}
                />
              </div>
            )}
            
            {/* Assunto e Pauta */}
            <div className="space-y-2">
              <Label>Assunto</Label>
              <Input
                placeholder="Ex: Desempenho acadêmico"
                value={formData.assunto}
                onChange={(e) => setFormData({ ...formData, assunto: e.target.value })}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Pauta</Label>
              <Textarea
                placeholder="Pontos a serem discutidos..."
                value={formData.pauta}
                onChange={(e) => setFormData({ ...formData, pauta: e.target.value })}
                rows={3}
              />
            </div>
            
            {/* Convidar Professores */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                Convidar Professores
              </Label>
              <div className="border rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
                {professores.length > 0 ? (
                  professores.map(prof => (
                    <div 
                      key={prof.id}
                      className="flex items-center gap-3"
                    >
                      <Checkbox
                        id={`prof-${prof.id}`}
                        checked={formData.professores_ids?.includes(prof.id)}
                        onCheckedChange={() => handleToggleProfessor(prof.id)}
                      />
                      <label htmlFor={`prof-${prof.id}`} className="flex-1 cursor-pointer">
                        <p className="text-sm font-medium">{prof.name}</p>
                        <p className="text-xs text-muted-foreground">{prof.cargo}</p>
                      </label>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-2">
                    Nenhum professor disponível
                  </p>
                )}
              </div>
              {formData.professores_ids?.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  {formData.professores_ids.length} professor(es) selecionado(s)
                </p>
              )}
            </div>
            
            {/* Observações */}
            <div className="space-y-2">
              <Label>Observações</Label>
              <Textarea
                placeholder="Observações adicionais..."
                value={formData.observacoes}
                onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setNovaReuniaoDialogOpen(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleCreateReuniao} 
              disabled={saving}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Agendando...
                </>
              ) : (
                <>
                  <Calendar className="w-4 h-4 mr-2" />
                  Agendar Reunião
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Dialog Detalhes da Reunião */}
      <Dialog open={detalhesDialogOpen} onOpenChange={setDetalhesDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <CalendarDays className="w-5 h-5 text-purple-600" />
                Detalhes da Reunião
              </span>
              {selectedReuniao && getStatusBadge(selectedReuniao.status)}
            </DialogTitle>
          </DialogHeader>
          
          {selectedReuniao && (
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-lg">{selectedReuniao.titulo}</h3>
                {selectedReuniao.assunto && (
                  <p className="text-muted-foreground">{selectedReuniao.assunto}</p>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <span>{new Date(selectedReuniao.data + 'T12:00:00').toLocaleDateString('pt-BR')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  <span>{selectedReuniao.hora_inicio} - {selectedReuniao.hora_fim}</span>
                </div>
                <div className="flex items-center gap-2">
                  {selectedReuniao.tipo === 'online' ? (
                    <Video className="w-4 h-4 text-muted-foreground" />
                  ) : (
                    <MapPin className="w-4 h-4 text-muted-foreground" />
                  )}
                  <span>{selectedReuniao.local}</span>
                </div>
              </div>
              
              {selectedReuniao.student_name && (
                <div className="p-3 bg-purple-50 rounded-lg">
                  <p className="text-sm font-medium flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Aluno: {selectedReuniao.student_name}
                  </p>
                  {selectedReuniao.responsavel_name && (
                    <p className="text-sm text-muted-foreground mt-1">
                      Responsável: {selectedReuniao.responsavel_name}
                    </p>
                  )}
                  {selectedReuniao.responsavel_phone && (
                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                      <Phone className="w-3 h-3" />
                      {selectedReuniao.responsavel_phone}
                    </p>
                  )}
                </div>
              )}
              
              {selectedReuniao.pauta && (
                <div>
                  <Label className="text-xs text-muted-foreground">PAUTA</Label>
                  <p className="text-sm mt-1">{selectedReuniao.pauta}</p>
                </div>
              )}
              
              {/* Professores Convidados */}
              {selectedReuniao.convites?.length > 0 && (
                <div>
                  <Label className="text-xs text-muted-foreground">PROFESSORES CONVIDADOS</Label>
                  <div className="mt-2 space-y-2">
                    {selectedReuniao.convites.map(convite => (
                      <div key={convite.id} className="flex items-center justify-between p-2 bg-muted rounded">
                        <span className="text-sm">{convite.professor_name}</span>
                        <Badge className={
                          convite.status === 'confirmado' ? 'bg-green-100 text-green-800' :
                          convite.status === 'recusado' ? 'bg-red-100 text-red-800' :
                          'bg-amber-100 text-amber-800'
                        }>
                          {convite.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {selectedReuniao.link_online && selectedReuniao.tipo === 'online' && (
                <a 
                  href={selectedReuniao.link_online}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 p-3 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
                >
                  <Video className="w-5 h-5" />
                  Entrar na Reunião Online
                </a>
              )}
              
              {canEdit() && selectedReuniao.status !== 'cancelada' && (
                <div className="pt-4 border-t flex justify-end gap-2">
                  <Button variant="destructive" onClick={handleCancelReuniao}>
                    <XCircle className="w-4 h-4 mr-2" />
                    Cancelar Reunião
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
